<template>
  <div :class="classes.statistic">
    <div :class="classes.text">
      <DynamicText
        tag="span"
        styleGroup="Statistics Text"
        :get="textStyle.get"
        :set="textStyle.set"
      >
        {{ statText }}
      </DynamicText>
    </div>
    <div :class="classes.sliderWrapper">
      <div :class="[classes.text, classes.sideValues]">
        <DynamicText
          tag="span"
          styleGroup="Statistics Text"
          :get="valueStyle.get"
          :set="valueStyle.set"
        >
          {{ formatedLeftValue }}
        </DynamicText>
      </div>
      <div ref="statisticsEl" :class="classes.slider">
        <div
          :class="[classes.left, 'left']"
          :style="{
            width: `${reversed ? rightSize : leftSize}%`
          }"
        ></div>
        <div :class="[classes.circle, 'divider']"></div>
        <div
          :class="[classes.right, 'right']"
          :style="{
            width: `${reversed ? leftSize : rightSize}%`
          }"
        ></div>
      </div>
      <div :class="[classes.text, classes.sideValues]">
        <DynamicText
          tag="span"
          styleGroup="Statistics Text"
          :get="valueStyle.get"
          :set="valueStyle.set"
        >
          {{ formatedRightValue }}
        </DynamicText>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import {
  BrandContext,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { Brand } from "@fresh8/types/src";
import DynamicText from "./DynamicText.vue";
import { composePopoverForm } from "@fresh8/ad-data-composition/src/popoverForm";
import { getTranslation } from "@fresh8/ad-data-composition/src/lang";
import { H2HFormatedData, FORMAT_KEYS } from "../hooks/useH2HStatistics";

const useStyles = createUseStyles({
  statistic: {
    display: "flex",
    flexDirection: "column"
  },
  text: {
    fontSize: "10px",
    lineHeight: "14px",
    textAlign: "center"
  },
  sideValues: {
    minWidth: "22px",
    width: "25px",
    height: "12px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  sliderWrapper: {
    marginTop: "3px",
    display: "flex",
    flexDirection: "row",
    gap: "4px"
  },
  slider: {
    width: "100%",
    display: "flex",
    marginTop: "1px"
  },
  left: {
    height: "8px",
    border: props => `1px solid ${props.borderColor}`,
    background: props => props.leftColor,
    borderRadius: "4px 0 0 4px",
    paddingRight: "5px"
  },
  right: {
    height: "8px",
    border: props => `1px solid ${props.borderColor}`,
    background: props => props.rigthColor,
    borderRadius: "0 4px 4px 0",
    paddingLeft: "5px"
  },
  circle: {
    minWidth: "11px",
    maxWidth: "11px",
    minHeight: "11px",
    maxHeight: "11px",
    border: props => `1px solid ${props.borderColor}`,
    background: props => props.dividerColor,
    top: "-2px",
    position: "relative",
    borderRadius: "6px",
    margin: "0 -10px 0 -10px"
  }
});

export const round = (value: number, placement?: number): number => {
  if (!placement || placement == 0) return Math.round(value + Number.EPSILON);
  const decimals = Math.pow(10, placement);
  return Math.round((value + Number.EPSILON) * decimals) / decimals;
};

const formatMap: Record<
  H2HFormatedData["format"],
  (value: number) => string
> = {
  [FORMAT_KEYS.DECIMAL_ONE]: (value: number) => {
    return round(value, 1).toFixed(1);
  },
  [FORMAT_KEYS.DECIMAL_TWO]: (value: number) => {
    return round(value, 2).toFixed(2);
  },
  [FORMAT_KEYS.LEADING]: (value: number) => {
    return round(value, 3)
      .toString()
      .substring(1)
      .padEnd(4, "0");
  },
  [FORMAT_KEYS.WHOLE]: (value: number) => {
    return round(value, 2).toFixed(0);
  }
};

export default defineComponent({
  components: {
    DynamicText
  },
  props: {
    text: {
      type: String,
      required: true
    },
    leftValue: {
      type: Number,
      required: true,
      default: 0
    },
    rightValue: {
      type: Number,
      required: true,
      default: 0
    },
    leftColor: {
      type: String,
      required: false,
      default: undefined
    },
    rightColor: {
      type: String,
      required: false,
      default: undefined
    },
    reversed: {
      type: Boolean,
      required: false,
      default: false
    },
    format: {
      type: String as PropType<H2HFormatedData["format"]>,
      required: true
    }
  },
  setup(props) {
    const { adTemplate, getColorStyle } = injectBrand() || ({} as BrandContext);

    const classesRef = useStyles(
      computed(() => ({
        leftColor:
          props.leftColor ||
          getColorStyle(adTemplate.value?.h2hCard?.homeTeamColor),
        rigthColor:
          props.rightColor ||
          getColorStyle(adTemplate.value?.h2hCard?.awayTeamColor),
        borderColor: getColorStyle(
          adTemplate.value?.h2hCard?.statisticBorderColor
        ),
        dividerColor: getColorStyle(
          adTemplate.value?.h2hCard?.statisticDividerColor
        )
      }))
    );
    const { targetEl: statisticsEl } = composePopoverForm("Match Statistics", [
      {
        type: "color",
        name: "Left color",
        get: () => adTemplate.value?.h2hCard?.homeTeamColor || "",
        set: (value: string) => {
          if (adTemplate?.value?.h2hCard) {
            adTemplate.value.h2hCard.homeTeamColor = value;
          }
        }
      },
      {
        type: "color",
        name: "Right color",
        get: () => adTemplate.value?.h2hCard?.awayTeamColor || "",
        set: (value: string) => {
          if (adTemplate.value?.h2hCard) {
            adTemplate.value.h2hCard.awayTeamColor = value;
          }
        }
      },
      {
        type: "color",
        name: "Border color",
        get: () => adTemplate.value?.h2hCard?.statisticBorderColor || "",
        set: (value: string) => {
          if (adTemplate.value?.h2hCard) {
            adTemplate.value.h2hCard.statisticBorderColor = value;
          }
        }
      },
      {
        type: "color",
        name: "Divider color",
        get: () => adTemplate.value?.h2hCard?.statisticDividerColor || "",
        set: (value: string) => {
          if (adTemplate.value?.h2hCard) {
            adTemplate.value.h2hCard.statisticDividerColor = value;
          }
        }
      }
    ]);

    return {
      formatedLeftValue: computed(() =>
        formatMap[props.format](props.leftValue)
      ),
      formatedRightValue: computed(() =>
        formatMap[props.format](props.rightValue)
      ),
      leftSize: computed(() => {
        const { leftValue, rightValue } = props;
        if (leftValue == 0 && rightValue == 0) {
          return 50;
        }
        return leftValue / ((leftValue + rightValue) / 100);
      }),
      rightSize: computed(() => {
        const { leftValue, rightValue } = props;
        if (leftValue == 0 && rightValue == 0) {
          return 50;
        }
        return rightValue / ((leftValue + rightValue) / 100);
      }),
      statText: computed(
        () =>
          getTranslation(window.navigator.language.toLowerCase()).H2HTexts[
            props.text
          ] || props.text
      ),
      statisticsEl,
      classes: classesRef,
      valueStyle: {
        get: (brand: Brand | undefined) => {
          if (brand?.adTemplate?.h2hCard?.statisticValueFont) {
            return brand?.adTemplate?.h2hCard?.statisticValueFont;
          }
        },
        set: (brand: Brand, value: string) => {
          if (brand?.adTemplate?.h2hCard?.statisticValueFont) {
            brand.adTemplate.h2hCard.statisticValueFont = value;
          }
        }
      },
      textStyle: {
        get: (brand: Brand | undefined) => {
          if (brand?.adTemplate?.h2hCard?.statisticTextFont) {
            return brand.adTemplate.h2hCard.statisticTextFont;
          }
        },
        set: (brand: Brand | undefined, value: string) => {
          if (brand?.adTemplate?.h2hCard?.statisticTextFont) {
            brand.adTemplate.h2hCard.statisticTextFont = value;
          }
        }
      }
    };
  }
});
</script>
